import jexl from 'jexl';
import { toLocalDateString, toLocalDateTimeString } from './localDateTime';

/**
 * Jexl context for default value expressions (e.g. demandDate).
 * Exposes today, today(offset), and the current row.
 */
function buildDefaultContext(row: Record<string, unknown>): Record<string, unknown> {
  const now = new Date();
  // Local calendar day: the UTC date of toISOString() is still yesterday shortly after
  // midnight east of Greenwich (review round 5, finding 1.10).
  const todayStr = toLocalDateString(now);
  const todayPlus1 = new Date(now);
  todayPlus1.setDate(todayPlus1.getDate() + 1);
  const todayPlus1Str = toLocalDateString(todayPlus1);

  return {
    today: todayStr,
    todayPlus1: todayPlus1Str,
    now: toLocalDateTimeString(now),
    ...row
  };
}

/**
 * Register Jexl function for date offset: today(0)=today, today(1)=tomorrow.
 */
function ensureDateFunctions(): void {
  if ((jexl as any)._dateFunctionsAdded) return;
  jexl.addFunction('today', (offset?: number) => {
    const d = new Date();
    d.setDate(d.getDate() + (offset ?? 0));
    return toLocalDateString(d);
  });
  (jexl as any)._dateFunctionsAdded = true;
}

/** Prefix/suffix for Jexl expressions in default values (SpEL-style). Only {# ... } is evaluated. */
const EXPRESSION_PREFIX = '{#';
const EXPRESSION_SUFFIX = '}';

/**
 * True if the value is a Jexl expression: starts with {# and ends with }.
 * Example: "{#today(1)}" → true; "BBN" → false.
 */
export function isDefaultValueExpression(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length > EXPRESSION_PREFIX.length + EXPRESSION_SUFFIX.length &&
    trimmed.startsWith(EXPRESSION_PREFIX) &&
    trimmed.endsWith(EXPRESSION_SUFFIX);
}

/**
 * Evaluate a default value: literal or Jexl expression.
 * Expression: value wrapped in {# ... } is evaluated (e.g. {#today(1)}, {#column['Foo']}).
 * Literal: everything else is returned as-is (e.g. BBN, today(1) without braces).
 */
export function evaluateDefaultValue(
  expression: string,
  row: Record<string, unknown>
): string | number | boolean {
  if (!expression || typeof expression !== 'string') return expression as string;
  const trimmed = expression.trim();
  if (!isDefaultValueExpression(trimmed)) {
    return trimmed;
  }
  const inner = trimmed.slice(EXPRESSION_PREFIX.length, -EXPRESSION_SUFFIX.length).trim();
  ensureDateFunctions();
  const context = buildDefaultContext(row);
  try {
    const result = jexl.evalSync(inner, context);
    return result != null ? String(result) : trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Evaluate row filter expression (Jexl). Context is the row (column names = keys).
 * Returns true if row should be included.
 */
export function evaluateRowFilter(
  rowFilter: string | undefined,
  row: Record<string, unknown>
): boolean {
  if (!rowFilter || !rowFilter.trim()) return true;
  try {
    const result = jexl.evalSync(rowFilter.trim(), row);
    return Boolean(result);
  } catch {
    return false;
  }
}
